import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Paperclip, Trash2 } from 'lucide-react';
import { getBill, updateBill } from '../../../../services/finance_services/billService';
import { getBillAttachments, uploadBillAttachment, deleteBillAttachment } from '../../../../services/finance_attachment_services/billAttachmentService';
import ReportForm from '../../../../components/ReportForm';
import Message from '../../../../components/Message';
import useApi from '../../../../hooks/useApi';

const EditBillPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [billNumber, setBillNumber] = useState('');
  const [dateIssued, setDateIssued] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [vendor, setVendor] = useState('');
  const [amountPaid, setAmountPaid] = useState('');
  const [paymentDate, setPaymentDate] = useState('');
  const [paymentReference, setPaymentReference] = useState('');
  const [files, setFiles] = useState([]);
  const [existingAttachments, setExistingAttachments] = useState([]);
  const [deletingAttachments, setDeletingAttachments] = useState([]);
  
  // Use useApi for fetching bill
  const { 
    loading: initialLoading, 
    error: initialError, 
    callApi: fetchBill 
  } = useApi(getBill, {showSuccessToast: false});
  
  // Use useApi for updating bill
  const { 
    loading, 
    error, 
    successMessage, 
    callApi: updateBillApi, 
    setError, 
    setSuccessMessage 
  } = useApi(updateBill, {showSuccessToast: true, msg: "Bill updated successfully"});

  useEffect(() => {
    const fetchData = async () => {
      try {
        const billResp = await fetchBill(id);
        if (!billResp) {
          return;
        }
        setTitle(billResp.title || '');
        setDescription(billResp.description || '');
        setAmount(billResp.amount !== undefined && billResp.amount !== null ? String(billResp.amount) : '');
        setBillNumber(billResp.bill_number || '');
        setDateIssued(billResp.date_issued ? new Date(billResp.date_issued).toISOString().slice(0, 10) : '');
        setDueDate(billResp.due_date ? new Date(billResp.due_date).toISOString().slice(0, 10) : '');
        setVendor(billResp.vendor || '');
        setAmountPaid(billResp.amount_paid !== undefined && billResp.amount_paid !== null ? String(billResp.amount_paid) : '');
        setPaymentDate(billResp.payment_date ? new Date(billResp.payment_date).toISOString().slice(0, 10) : '');
        setPaymentReference(billResp.payment_reference || '');
        
        // Fetch attachments
        const attachments = await getBillAttachments(id);
        setExistingAttachments(attachments?.data || []);
      } catch (err) {
        console.error('Failed to load bill:', err);
      }
    };
    fetchData();
  }, [id]);

  const handleFileChange = (e) => {
    setFiles([...e.target.files]);
  };

  const handleDeleteAttachment = async (attachmentId) => {
    try {
      setDeletingAttachments(prev => [...prev, attachmentId]);
      await deleteBillAttachment(id, attachmentId);
      setExistingAttachments(prev => prev.filter(attachment => attachment.id !== attachmentId));
    } catch (err) {
      setError(err.message);
    } finally {
      setDeletingAttachments(prev => prev.filter(id => id !== attachmentId));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const billData = {
      title,
      description,
      amount,
      bill_number: billNumber,
      date_issued: dateIssued,
      due_date: dueDate,
      vendor,
      amount_paid: amountPaid,
      payment_date: paymentDate || null,
      payment_reference: paymentReference,
    };
    try {
      await updateBillApi(id, billData);
      if (files.length > 0) {
        await Promise.all(
          files.map(file => {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('title', file.name);
            return uploadBillAttachment(id, formData);
          })
        );
      }
      setSuccessMessage('Bill updated successfully!');
      setTimeout(() => {
        navigate('/dashboard/bills');
      }, 1500);
    } catch (err) {
      setError(err.message);
    }
  };

  if (initialLoading) {
    return (
      <div className="flex justify-center items-center min-h-[300px]">
        <span className="text-gray-500">Loading...</span>
      </div>
    );
  }
  if (initialError) {
    return (
      <div className="flex flex-col justify-center items-center min-h-[300px] space-y-2">
        <Message type="error" message={initialError} />
      </div>
    );
  }

  // Define form fields for ReportForm
  const formFields = [
    { label: 'Title', type: 'text', value: title, onChange: e => setTitle(e.target.value), required: true },
    { label: 'Description', type: 'textarea', value: description, onChange: e => setDescription(e.target.value) },
    { label: 'Amount', type: 'number', value: amount, onChange: e => setAmount(e.target.value), required: true },
    { label: 'Bill Number', type: 'text', value: billNumber, onChange: e => setBillNumber(e.target.value) },
    { label: 'Date Issued', type: 'date', value: dateIssued, onChange: e => setDateIssued(e.target.value), required: true },
    { label: 'Due Date', type: 'date', value: dueDate, onChange: e => setDueDate(e.target.value), required: true },
    { label: 'Vendor', type: 'text', value: vendor, onChange: e => setVendor(e.target.value) },
    { label: 'Amount Paid', type: 'number', value: amountPaid, onChange: e => setAmountPaid(e.target.value) },
    { label: 'Payment Date', type: 'date', value: paymentDate, onChange: e => setPaymentDate(e.target.value) },
    { label: 'Payment Reference', type: 'text', value: paymentReference, onChange: e => setPaymentReference(e.target.value) },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => navigate('/dashboard/bills')}
          className="inline-flex items-center px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Bills
        </button>
        <h1 className="text-2xl font-bold text-gray-900">Edit Bill</h1>
      </div>

      {/* Display Messages */}
      <Message type="error" message={error} />
      <Message type="success" message={successMessage} />
      
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <ReportForm
        title="Edit Bill"
        fields={formFields}
        onSubmit={handleSubmit}
        onCancel={() => navigate('/dashboard/bills')}
        loading={loading}
        submitButtonText="Update Bill"
      >
        {/* Existing Attachments */}
        {existingAttachments.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-gray-700">Current Attachments</label>
            <ul className="mt-2 space-y-2">
              {existingAttachments.map(attachment => (
                <li key={attachment.id} className="flex items-center justify-between p-2 bg-gray-50 rounded-md">
                  <div className="flex items-center">
                    <Paperclip className="h-4 w-4 mr-2 text-gray-500" />
                    <span>{attachment.title || attachment.file_name}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleDeleteAttachment(attachment.id)}
                    className="text-red-600 hover:text-red-800"
                    disabled={deletingAttachments.includes(attachment.id)}
                  >
                    {deletingAttachments.includes(attachment.id) ? (
                      'Deleting...'
                    ) : (
                      <Trash2 className="h-4 w-4" />
                    )}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
        {/* New Attachments */}
        <div>
          <label htmlFor="attachments" className="block text-sm font-medium text-gray-700">
            Add New Attachments (Receipts, Invoices, etc.)
          </label>
          <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
            <div className="space-y-1 text-center">
              <Paperclip className="mx-auto h-12 w-12 text-gray-400" />
              <div className="flex text-sm text-gray-600">
                <label
                  htmlFor="file-upload"
                  className="relative cursor-pointer bg-white rounded-md font-medium text-primary-600 hover:text-primary-500 focus-within:outline-none"
                >
                  <span>Upload files</span>
                  <input
                    id="file-upload"
                    name="file-upload"
                    type="file"
                    className="sr-only"
                    multiple
                    onChange={handleFileChange}
                  />
                </label>
                <p className="pl-1">or drag and drop</p>
              </div>
              <p className="text-xs text-gray-500">
                PDF, JPG, PNG up to 10MB
              </p>
            </div>
            {files.length > 0 && (
              <div className="mt-2">
                <p className="text-sm text-gray-500">Files to upload:</p>
                <ul className="mt-1 text-sm text-gray-700">
                  {Array.from(files).map((file, index) => (
                    <li key={index} className="flex items-center">
                      <Paperclip className="h-4 w-4 mr-2 text-gray-500" />
                      {file.name} ({Math.round(file.size / 1024)} KB)
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </ReportForm>
      </div>
    </div>
  );
};

export default EditBillPage;